import React, { useState, useEffect, useCallback } from 'react';
import { AxiosAdminInstance } from '../utils';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

interface AdditionalPage {
    id: string;
    pagename: string;
    displayaslink: boolean;
    displaylinkname: string;
    pageurl: string; // Object storage path like "pages/<eventcode>/<pagename>"
    createdate?: string;
}

export const AdditionalPageManager = () => {
    const { eventcode: urlEventCode } = useParams<{ eventcode: string }>();
    const [pages, setPages] = useState<AdditionalPage[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingPage, setEditingPage] = useState<AdditionalPage | null>(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [newPage, setNewPage] = useState({
        pagename: '',
        displayaslink: false,
        displaylinkname: '',
        pageurl: '' // For create: holds HTML content; for edit: holds storage path from RTDB
    });
    const [editorContent, setEditorContent] = useState<string>(''); // HTML content loaded from / to be saved to storage
    const [contentLoading, setContentLoading] = useState(false);

    // Custom toolbar handler for fullscreen
    const handleFullscreen = useCallback(() => {
        setFullscreen(prev => !prev);
    }, []);

    useEffect(() => {
        if (urlEventCode) {
            fetchPages();
        }
    }, [urlEventCode]);

    useEffect(() => {
        if (editingPage) {
            setNewPage({
                pagename: editingPage.pagename,
                displayaslink: editingPage.displayaslink,
                displaylinkname: editingPage.displaylinkname,
                pageurl: editingPage.pageurl // This is storage path
            });
            // Load content from object storage for editing
            if (editingPage.pageurl) {
                loadContentFromStorage(editingPage.pageurl);
            } else {
                setEditorContent('');
            }
        } else {
            setNewPage({
                pagename: '',
                displayaslink: false,
                displaylinkname: '',
                pageurl: ''
            });
            setEditorContent('');
        }
    }, [editingPage]);

    const fetchPages = async () => {
        if (!urlEventCode) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.get(`/api/admin/manageAdditionalPages`, {
                params: { eventcode: urlEventCode }
            });
            if (response.data.result === 'Success') {
                setPages(response.data.data || []);
            } else {
                toast.error(response.data.msg || 'Failed to fetch pages');
            }
        } catch (error) {
            toast.error('Failed to fetch pages');
        }
        setLoading(false);
    };

    const loadContentFromStorage = async (storagePath: string) => {
        setContentLoading(true);
        try {
            const { data } = await AxiosAdminInstance.get('/api/admin/getPage', { params: { path: storagePath } });
            setEditorContent(data.content || '');
        } catch (error) {
            console.log('Error loading content from storage:', error);
            setEditorContent('');
        }
        setContentLoading(false);
    };

    const handleSavePage = async () => {
        if (!newPage.pagename.trim()) {
            toast.error('Page name is required');
            return;
        }
        if (!urlEventCode) {
            toast.error('Event code is required');
            return;
        }

        setLoading(true);
        try {
            const trimmedPagename = newPage.pagename.trim().toLowerCase().replace(/\s+/g, '_');
            const storagePath = `pages/${urlEventCode}/${trimmedPagename}`;

            if (editingPage) {
                // Use existing storage path if available, otherwise use new path
                const targetStoragePath = editingPage.pageurl || storagePath;

                // 1. Save HTML content to Firebase Storage (overwrite existing)
                await AxiosAdminInstance.post('/api/admin/savePage', {
                    path: targetStoragePath,
                    content: editorContent
                });

                // 2. Update metadata in RTDB
                await AxiosAdminInstance.put('/api/admin/manageAdditionalPages', {
                    pagename: newPage.pagename,
                    displayaslink: newPage.displayaslink,
                    displaylinkname: newPage.displaylinkname,
                    pageurl: targetStoragePath
                }, {
                    params: { eventcode: urlEventCode, pageId: editingPage.id }
                });

                toast.success('Page updated successfully');
            } else {
                // 1. Create HTML content in Firebase Storage
                await AxiosAdminInstance.post('/api/admin/createPage', {
                    path: storagePath,
                    content: editorContent
                });

                // 2. Create metadata entry in RTDB
                const response = await AxiosAdminInstance.post('/api/admin/manageAdditionalPages', {
                    pagename: newPage.pagename,
                    displayaslink: newPage.displayaslink,
                    displaylinkname: newPage.displaylinkname,
                    pageurl: storagePath
                }, {
                    params: { eventcode: urlEventCode }
                });

                if (response.data.result !== 'Success') {
                    toast.error(response.data.msg || 'Failed to create page');
                    setLoading(false);
                    return;
                }

                toast.success('Page created successfully');
            }

            setEditingPage(null);
            setNewPage({
                pagename: '',
                displayaslink: false,
                displaylinkname: '',
                pageurl: ''
            });
            setEditorContent('');
            fetchPages();
        } catch (error: any) {
            const errMsg = error?.response?.data?.result || error?.response?.data?.msg || '';
            if (errMsg === 'File already exists') {
                toast.error('A page with this name already exists in storage. Choose a different name.');
            } else {
                toast.error(editingPage ? 'Failed to update page' : 'Failed to create page');
            }
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewPage({
            pagename: '',
            displayaslink: false,
            displaylinkname: '',
            pageurl: ''
        });
        setEditorContent('');
        setEditingPage(null);
    };

    const handleDeletePage = async (pageId: string) => {
        if (!window.confirm('Are you sure you want to delete this page?')) return;
        if (!urlEventCode) return;

        const pageToDelete = pages.find(p => p.id === pageId);
        if (!pageToDelete) return;

        setLoading(true);
        try {
            // 1. Delete from Firebase Storage if pageurl exists
            if (pageToDelete.pageurl) {
                try {
                    await AxiosAdminInstance.delete('/api/admin/deletePage', {
                        params: { path: pageToDelete.pageurl }
                    });
                } catch (storageError) {
                    // Ignore storage deletion errors, continue with RTDB deletion
                    console.log('Storage deletion error (may not exist):', storageError);
                }
            }

            // 2. Delete metadata from RTDB
            const response = await AxiosAdminInstance.delete('/api/admin/manageAdditionalPages', {
                params: { eventcode: urlEventCode, pageId }
            });

            if (response.data.result === 'Success') {
                toast.success('Page deleted successfully');
                fetchPages();
            } else {
                toast.error(response.data.msg || 'Failed to delete page');
            }
        } catch (error) {
            toast.error('Failed to delete page');
        } finally {
            setLoading(false);
        }
    };

    const pageColumns = [
        {
            name: 'Actions',
            cell: (row: AdditionalPage) => (
                <div>
                    <button type="button" className="btn btn-sm btn-warning me-1" onClick={() => setEditingPage(row)} title="Edit">
                        <i className="fa fa-edit"></i>
                    </button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDeletePage(row.id)} title="Delete">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ),
            width: '120px'
        },
        {
            name: 'Page Name',
            selector: (row: AdditionalPage) => row.pagename,
            sortable: true,
            width: '200px'
        },
        {
            name: 'Display As Link',
            cell: (row: AdditionalPage) => (
                <span>{row.displayaslink ? 'Yes' : 'No'}</span>
            ),
            sortable: true,
            width: '150px'
        },
        {
            name: 'Link Name',
            selector: (row: AdditionalPage) => row.displaylinkname || '-',
            sortable: true,
            width: '200px'
        },
        {
            name: 'Storage Path',
            cell: (row: AdditionalPage) => (
                <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.pageurl}>
                    {row.pageurl || '-'}
                </div>
            ),
            sortable: false,
            width: '250px'
        }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-file fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">
                            Additional Page Manager {urlEventCode && <small>- {urlEventCode}</small>}
                        </h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleSavePage(); }} noValidate>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingPage ? 'Edit Page' : 'Add New Page'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="pageName">Page Name</label>
                                            <input
                                                type="text"
                                                id="pageName"
                                                className="form-control"
                                                placeholder="Page Name"
                                                value={newPage.pagename}
                                                onChange={(e) => setNewPage(prev => ({ ...prev, pagename: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="displayAsLink">Display As Link</label>
                                            <div className="form-check" style={{ paddingTop: '10px' }}>
                                                <input
                                                    type="checkbox"
                                                    id="displayAsLink"
                                                    className="form-check-input"
                                                    checked={newPage.displayaslink}
                                                    onChange={(e) => setNewPage(prev => ({ ...prev, displayaslink: e.target.checked }))}
                                                    style={{ width: '20px', height: '20px' }}
                                                />
                                                <label className="form-check-label" htmlFor="displayAsLink" style={{ paddingLeft: '10px', cursor: 'pointer' }}>
                                                    {newPage.displayaslink ? 'Yes' : 'No'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {newPage.displayaslink && (
                                    <div className="row pt-2">
                                        <div className="col-lg-6 col-md-6 col-sm-12">
                                            <div className="form-group">
                                                <label htmlFor="displayLinkName">Display Link Name</label>
                                                <input
                                                    type="text"
                                                    id="displayLinkName"
                                                    className="form-control"
                                                    placeholder="Display Link Name"
                                                    value={newPage.displaylinkname}
                                                    onChange={(e) => setNewPage(prev => ({ ...prev, displaylinkname: e.target.value }))}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="pageContent">Page Content</label>
                                            {contentLoading && <span className="ms-2"><i className="fa fa-circle-o-notch fa-spin"></i> Loading content...</span>}
                                            <div className={fullscreen ? 'editor-fullscreen' : 'editor-normal'}>
                                                <ReactQuill
                                                    id="pageContent"
                                                    value={editorContent}
                                                    onChange={(value) => setEditorContent(value)}
                                                    style={{ height: '300px', background: 'white' }}
                                                    theme="snow"
                                                    modules={{
                                                        toolbar: {
                                                            container: [
                                                                [{ 'header': [1, 2, 3, false] }],
                                                                ['bold', 'italic', 'underline', 'strike'],
                                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                                ['link', 'image'],
                                                                ['clean'],
                                                                ['fullscreen']
                                                            ],
                                                            handlers: {
                                                                'fullscreen': handleFullscreen
                                                            }
                                                        }
                                                    }}
                                                    formats={[
                                                        'header',
                                                        'bold', 'italic', 'underline', 'strike',
                                                        'list', 'bullet',
                                                        'link', 'image'
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn-default" type="submit" disabled={loading}>
                                            {editingPage ? 'Update Page' : 'Add Page'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingPage && (
                                            <button className="btn-default" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Pages</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={pageColumns}
                                            data={pages}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};